import React, { useState, useEffect, useRef } from 'react';
import { Box, IconButton, Typography, Paper, TextField, Button } from '@mui/material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CloseIcon from '@mui/icons-material/Close';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import MessageIcon from '@mui/icons-material/Message';

const FloatingChatButton = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [userMessage, setUserMessage] = useState('');
  const [chatMessages, setChatMessages] = useState([]);
  const [sessionId] = useState(() => Math.random().toString(36).substring(7));
  const [isBotTyping, setIsBotTyping] = useState(false);
  const messagesEndRef = useRef(null);

  // Ajusta tu número de teléfono y mensaje por defecto aquí
  const phoneNumber = "521234567890";
  const defaultMessage = "Hola, necesito ayuda";

  // Ajusta (o quita) el "delay artificial" para que se aprecie la animación "typing"
  const artificialDelay = 1500; // ms

  const handleWhatsAppClick = () => {
    const encodedMessage = encodeURIComponent(defaultMessage);
    const url = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
    window.open(url, "_blank");
  };

  const toggleChatbot = () => {
    if (isOpen) {
      setIsAnimating(true);
      setTimeout(() => {
        setIsAnimating(false);
        setIsOpen(false);
      }, 500);
    } else {
      setIsOpen(true);

      // Mostrar mensaje inicial con retraso al abrir
      setTimeout(() => {
        if (chatMessages.length === 0) {
          setChatMessages([
            { text: '¡Hola! ¿En qué puedo ayudarte?', isBot: true, noAnswer: false },
          ]);
        }
      }, 500);
    }
    setShowNotification(false);
  };

  const handleSendMessage = async () => {
    if (userMessage.trim() === '') return;

    // Mensaje de usuario
    const userChatMessage = {
      text: userMessage,
      isBot: false,
      noAnswer: false,
    };
    setChatMessages((prev) => [...prev, userChatMessage]);

    // Activamos la animación de "escribiendo..."
    setIsBotTyping(true);

    try {
      // (Opcional) Esperar un poco antes de llamar al backend
      await new Promise(resolve => setTimeout(resolve, artificialDelay));

      const response = await fetch('https://cb.imdituxtla.com.mx/chatbot.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: userMessage, session_id: sessionId }),
      });

      const data = await response.json();

      const botResponse = {
        text: data.reply || "Lo siento, no obtuve respuesta. 😕",
        isBot: true,
        noAnswer: data.noAnswer || false,
      };
      setChatMessages((prev) => [...prev, botResponse]);
    } catch (error) {
      console.error("Error al conectar con el servidor:", error);
      setChatMessages((prev) => [
        ...prev,
        {
          text: "Hubo un error al procesar tu solicitud. Inténtalo más tarde. 😞",
          isBot: true,
          noAnswer: false,
        },
      ]);
    } finally {
      setIsBotTyping(false);
    }

    setUserMessage('');
  };

  useEffect(() => {
    // Scrollea siempre al último mensaje
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollTop = messagesEndRef.current.scrollHeight;
    }
  }, [chatMessages, isBotTyping]);

  useEffect(() => {
    const scrollThreshold = document.documentElement.scrollHeight * 0.25;
    let prevScrollY = 0;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (currentScrollY > scrollThreshold && prevScrollY <= scrollThreshold && !isOpen) {
        setIsVisible(true);
        setShowNotification(true);
      } else if (currentScrollY <= scrollThreshold && prevScrollY > scrollThreshold) {
        setIsVisible(false);
        setTimeout(() => setShowNotification(false), 500);
      }
      prevScrollY = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isOpen]);

  return (
    <Box>
      {/* Botón flotante */}
      <Box sx={{ position: 'fixed', bottom: 20, right: 20, zIndex: 1000 }}>
        {showNotification && !isOpen && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 10,
              right: 80,
              backgroundColor: '#ffffff',
              color: '#333',
              padding: '8px 12px',
              borderRadius: '12px',
              boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.2)',
              whiteSpace: 'nowrap',
              animation: `${isVisible ? 'bounceIn' : 'fadeOut'} 0.5s ease`,
              '@keyframes bounceIn': {
                '0%': { opacity: 0, transform: 'scale(0.8)' },
                '50%': { opacity: 0.5, transform: 'scale(1.2)' },
                '100%': { opacity: 1, transform: 'scale(1)' },
              },
              '@keyframes fadeOut': {
                '0%': { opacity: 1 },
                '100%': { opacity: 0 },
              },
            }}
            onClick={toggleChatbot}
          >
            <Typography variant="body2" sx={{ fontSize: '14px' }}>
              ¿Tienes alguna duda? 😊
            </Typography>
          </Box>
        )}

        <IconButton
          onClick={toggleChatbot}
          sx={{
            backgroundColor: 'rgb(51, 64, 64)',
            color: 'white',
            '&:hover': {
              backgroundColor: '#1e5d74',
              transform: 'scale(1.1)',
              transition: 'transform 0.3s ease',
            },
            boxShadow: '0px 6px 12px rgba(0, 0, 0, 0.25)',
            width: 56,
            height: 56,
          }}
        >
          <ChatBubbleOutlineIcon fontSize="medium" />
        </IconButton>
      </Box>

      {/* Chat flotante */}
      {(isOpen || isAnimating) && (
        <Paper
          elevation={5}
          sx={{
            position: 'fixed',
            bottom: 80,
            right: 20,
            width: 350,
            height: 500,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 4,
            overflow: 'hidden',
            backgroundColor: '#ffffff',
            zIndex: 1000,
            animation: `${isAnimating ? 'slideOut' : 'slideIn'} 0.5s ease`,
            '@keyframes slideIn': {
              '0%': { opacity: 0, transform: 'translateY(100%)' },
              '100%': { opacity: 1, transform: 'translateY(0)' },
            },
            '@keyframes slideOut': {
              '0%': { opacity: 1, transform: 'translateY(0)' },
              '100%': { opacity: 0, transform: 'translateY(100%)' },
            },
          }}
        >
          {/* Encabezado */}
          <Box
            sx={{
              backgroundColor: 'rgb(51, 64, 64)',
              color: 'white',
              padding: '12px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            {/* Contenedor para ícono de mensaje + texto */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton onClick={toggleChatbot} sx={{ color: 'white' }}>
                <MessageIcon />
              </IconButton>
              <Typography variant="h6" sx={{ marginLeft: 1 }}>
                Asesor Virtual
              </Typography>
            </Box>

            {/* Ícono de cerrar a la derecha */}
            <IconButton onClick={toggleChatbot} sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Mensajes */}
          <Box
            ref={messagesEndRef}
            sx={{
              flex: 1,
              padding: '12px',
              backgroundColor: '#f0f4f8',
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
            }}
          >
            {chatMessages.map((message, index) => (
              <Box
                key={index}
                sx={{
                  alignSelf: message.isBot ? 'flex-start' : 'flex-end',
                  backgroundColor: message.isBot ? '#e5e5ea' : '#728895',
                  color: message.isBot ? '#000' : 'white',
                  padding: '10px 12px',
                  borderRadius: '18px',
                  maxWidth: '70%',
                  wordWrap: 'break-word',
                  animation: `${message.isBot ? 'slideInLeft' : 'slideInRight'} 0.5s ease`,
                  '@keyframes slideInLeft': {
                    '0%': { opacity: 0, transform: 'translateX(-20px)' },
                    '100%': { opacity: 1, transform: 'translateX(0)' },
                  },
                  '@keyframes slideInRight': {
                    '0%': { opacity: 0, transform: 'translateX(20px)' },
                    '100%': { opacity: 1, transform: 'translateX(0)' },
                  },
                }}
              >
                {message.text.split('\n').map((line, lineIndex) => (
                  <Typography
                    key={lineIndex}
                    sx={{
                      display: 'block',
                      marginBottom:
                        lineIndex === message.text.split('\n').length - 1
                          ? 0
                          : '4px',
                    }}
                  >
                    {line}
                  </Typography>
                ))}

                {/* Si es el bot y no tuvo respuesta, mostrar botón de WhatsApp */}
                {message.isBot && message.noAnswer && (
                  <Box sx={{ marginTop: '8px' }}>
                    <Button
                      variant="contained"
                      startIcon={<WhatsAppIcon />}
                      onClick={handleWhatsAppClick}
                      sx={{
                        backgroundColor: '#25D366',
                        color: '#fff',
                        fontSize: '0.8rem',
                        textTransform: 'none',
                        borderRadius: '16px',
                        marginTop: '4px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          backgroundColor: '#1ebe57',
                          transform: 'scale(1.05)',
                          boxShadow: '0 6px 18px rgba(0, 0, 0, 0.3)',
                        },
                      }}
                    >
                      Contactar por WhatsApp
                    </Button>
                  </Box>
                )}
              </Box>
            ))}

            {/* Indicador de "escribiendo..." si el bot está respondiendo */}
            {isBotTyping && (
              <Box
                sx={{
                  alignSelf: 'flex-start',
                  marginLeft: '10px',
                  display: 'flex',
                  // Suave transición para que no aparezca de golpe
                  opacity: 0,
                  animation: 'fadeInTyping 0.3s forwards ease-in-out',
                  '@keyframes fadeInTyping': {
                    '0%': { opacity: 0 },
                    '100%': { opacity: 1 },
                  },
                }}
              >
                <Box
                  sx={{
                    // Burbuja del "typing"
                    backgroundColor: '#e5e5ea',
                    color: '#000',
                    padding: '8px 12px',
                    borderRadius: '18px',
                    // Evita que se vea demasiado apretado
                    minWidth: '40px',
                    minHeight: '30px',
                    // Centra los puntitos
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <TypingIndicator />
                </Box>
              </Box>
            )}
          </Box>

          {/* Campo de entrada */}
          <Box sx={{ display: 'flex', padding: '12px', borderTop: '1px solid #ddd' }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Escribe tu mensaje..."
              value={userMessage}
              onChange={(e) => setUserMessage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
            />
            <Button
              variant="contained"
              onClick={handleSendMessage}
              sx={{
                px: { xs: 2, md: 3 },
                py: { xs: 0.8, md: 1.2 },
                fontSize: { xs: '0.8rem', md: '0.95rem' },
                fontWeight: 600,
                color: '#fff',
                backgroundColor: 'rgb(51, 64, 64)',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: 'rgb(41, 54, 54)',
                  transform: 'scale(1.05)',
                  boxShadow: '0 6px 18px rgba(0, 0, 0, 0.3)',
                },
              }}
            >
              Enviar
            </Button>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

/**
 * Componente “TypingIndicator” con animación refinada y llamativa (wave + escala + cambio de color).
 */
const TypingIndicator = () => {
  return (
    <Box sx={{ display: 'flex' }}>
      {/* Punto 1 */}
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          backgroundColor: '#bbb',
          marginRight: 1,
          animation: 'refinedWave 1.6s infinite ease-in-out',
          '@keyframes refinedWave': {
            '0%, 70%, 100%': {
              transform: 'translateY(0) scale(1)',
              backgroundColor: '#bbb',
              opacity: 0.7,
            },
            '35%': {
              transform: 'translateY(-6px) scale(1.15)',
              backgroundColor: '#333',
              opacity: 1,
            },
          },
        }}
      />
      {/* Punto 2 */}
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          backgroundColor: '#bbb',
          marginRight: 1,
          animation: 'refinedWave 1.6s infinite ease-in-out',
          animationDelay: '0.1s',
          '@keyframes refinedWave': {
            '0%, 70%, 100%': {
              transform: 'translateY(0) scale(1)',
              backgroundColor: '#bbb',
              opacity: 0.7,
            },
            '35%': {
              transform: 'translateY(-6px) scale(1.15)',
              backgroundColor: '#333',
              opacity: 1,
            },
          },
        }}
      />
      {/* Punto 3 */}
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          backgroundColor: '#bbb',
          animation: 'refinedWave 1.6s infinite ease-in-out',
          animationDelay: '0.2s',
          '@keyframes refinedWave': {
            '0%, 70%, 100%': {
              transform: 'translateY(0) scale(1)',
              backgroundColor: '#bbb',
              opacity: 0.7,
            },
            '35%': {
              transform: 'translateY(-6px) scale(1.15)',
              backgroundColor: '#333',
              opacity: 1,
            },
          },
        }}
      />
    </Box>
  );
};

export default FloatingChatButton;
